/*
 * <heritage-credit-application>
 *
 * Submission adapter for MDJ's credit-application form (the
 * Become-a-Partner page). Wraps the application-form section's
 * <form action="/contact">. On submit:
 *
 *   1. Read all form values into a map keyed by the inner form-field
 *      name (the bit inside `contact[...]`).
 *   2. Build a Braintree creditFormData payload that mirrors the
 *      LEGACY multistep-form contract (templates/page.multistep-form
 *      .liquid in the live MDJ theme). The legacy form collected ~70+
 *      fields across business, principal, bank, and trade-reference
 *      blocks; the new trimmed form collects ~24. Missing fields
 *      default to "-" so the Braintree endpoint receives a complete
 *      shape and the MDJ admin team's existing review workflow stays
 *      green.
 *   3. POST to the Braintree endpoint with `fetch`. We don't await
 *      indefinitely — a configurable timeout (default 6 s) prevents
 *      a stalled Braintree from blocking the partner's email.
 *   4. Submit the form natively. That triggers Shopify's contact
 *      handler, which sends the actionable email to MDJ and redirects
 *      to the same page with the success state rendered.
 *
 * Graceful degrade — if the Braintree token attribute is empty
 * (metafield not yet set in Shopify admin), the Braintree fetch is
 * skipped entirely. The Shopify contact email still goes out.
 *
 * Smadify integration (future) hooks in beside the Braintree call,
 * fire-and-forget like the memo / reserve / vault stores. Not in
 * scope here.
 *
 * Required host attributes:
 *   data-braintree-url    — endpoint URL
 *   data-braintree-token  — tenant public token (from shop metafield)
 *   data-submit-timeout   — ms; default 6000
 *
 * The form name->Braintree-field map is partial — fields with names
 * that already match the Braintree spec (e.g. business_name) pass
 * through; the JS adds derived fields (principle_fname/lname split
 * from a single Name input) and the "-" defaults.
 */

(function () {
  'use strict';

  /*
   * Canonical Braintree field list — every key the legacy multistep
   * form pushed into the credit endpoint. Missing values default to
   * "-" in the final payload. Keep this list in sync with the
   * /templates/page.multistep-form.liquid reference in the mdj-live
   * repo if the Braintree contract is ever expanded.
   */
  var BRAINTREE_FIELDS = [
    // Business
    'business_name', 'business_legal_name',
    'business_address_1', 'business_address_2', 'business_building',
    'business_city', 'business_state', 'business_canadian_provinces', 'business_zip',
    'business_federal_id', 'business_jbt_number',
    'business_phone', 'business_fax', 'business_email', 'business_website',
    // Principal (owner)
    'principle_fname', 'principle_lname',
    'principle_address_1', 'principle_address_2', 'principle_building',
    'principle_city', 'principle_state', 'principle_canadian_provinces', 'principle_zip',
    'principle_ssn', 'principle_phone',
    'principle_buyer_1_fname', 'principle_buyer_1_lname',
    'principle_buyer_2_fname', 'principle_buyer_2_lname',
    // Bank
    'bank_name',
    'bank_address_1', 'bank_address_2', 'bank_building',
    'bank_city', 'bank_state', 'bank_canadian_provinces', 'bank_zip',
    'bank_branch_name', 'bank_branch_contact_name', 'bank_branch_contact_phone',
    'bank_account_number',
    // Trade references (4 slots)
    'trade_ref_1_firm',
    'trade_ref_1_address_1', 'trade_ref_1_address_2', 'trade_ref_1_building',
    'trade_ref_1_city', 'trade_ref_1_state', 'trade_ref_1_canadian_provinces', 'trade_ref_1_zip',
    'trade_ref_1_phone', 'trade_ref_1_fax',
    'trade_ref_2_firm',
    'trade_ref_2_address_1', 'trade_ref_2_address_2', 'trade_ref_2_building',
    'trade_ref_2_city', 'trade_ref_2_state', 'trade_ref_2_canadian_provinces', 'trade_ref_2_zip',
    'trade_ref_2_phone', 'trade_ref_2_fax',
    'trade_ref_3_firm',
    'trade_ref_3_address_1', 'trade_ref_3_address_2', 'trade_ref_3_building',
    'trade_ref_3_city', 'trade_ref_3_state', 'trade_ref_3_canadian_provinces', 'trade_ref_3_zip',
    'trade_ref_3_phone', 'trade_ref_3_fax',
    'trade_ref_4_firm',
    'trade_ref_4_address_1', 'trade_ref_4_address_2', 'trade_ref_4_building',
    'trade_ref_4_city', 'trade_ref_4_state', 'trade_ref_4_canadian_provinces', 'trade_ref_4_zip',
    'trade_ref_4_phone', 'trade_ref_4_fax'
  ];

  /* Pattern to peel "contact[…]" wrappers from input names so the JS
     sees the raw Braintree-style key. */
  var CONTACT_NAME = /^contact\[([^\]]+)\]$/;

  class HeritageCreditApplication extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form[action*="/contact"]');
      if (!this.form) return;
      this.braintreeUrl = this.dataset.braintreeUrl || '';
      this.braintreeToken = this.dataset.braintreeToken || '';
      this.submitTimeout = parseInt(this.dataset.submitTimeout || '6000', 10) || 6000;
      this.draftKey = 'mdj.credit-app.draft';
      this.form.addEventListener('submit', this.handleSubmit);

      // Restore the partner's input after a validation error. Shopify
      // re-renders the page blank on a contact-form error (it only
      // hands back the 4 standard fields, not our custom-named ones),
      // so we stash the whole form in sessionStorage on submit and
      // re-apply it here. One-shot: cleared once read, so a fresh
      // visit never re-applies stale input. On the success state
      // there are no fields to fill, so this no-ops.
      this.restoreDraft();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    }

    /* Serialize every contact[...] field to sessionStorage. Called on
       submit so the page-reload after a validation error can repopulate. */
    saveDraft() {
      if (!this.form) return;
      var draft = {};
      var els = this.form.querySelectorAll(
        'input[name^="contact["], select[name^="contact["], textarea[name^="contact["]'
      );
      els.forEach(function (el) {
        if (el.type === 'checkbox' || el.type === 'radio') {
          draft[el.name] = el.checked ? (el.value || 'on') : '';
        } else {
          draft[el.name] = el.value;
        }
      });
      try { window.sessionStorage.setItem(this.draftKey, JSON.stringify(draft)); } catch (e) { /* storage unavailable */ }
    }

    /* Re-apply a saved draft into the rendered fields, then clear it
       (one-shot). No-ops when there's no draft or no matching fields
       (e.g. the success state). Matches by element, not querySelector,
       to sidestep escaping the bracketed contact[...] names. */
    restoreDraft() {
      var raw;
      try { raw = window.sessionStorage.getItem(this.draftKey); } catch (e) { return; }
      try { window.sessionStorage.removeItem(this.draftKey); } catch (e) { /* noop */ }
      if (!raw) return;
      var draft;
      try { draft = JSON.parse(raw); } catch (e) { return; }
      if (!draft) return;
      var els = this.form.querySelectorAll(
        'input[name^="contact["], select[name^="contact["], textarea[name^="contact["]'
      );
      els.forEach(function (el) {
        if (!Object.prototype.hasOwnProperty.call(draft, el.name)) return;
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = draft[el.name] !== '';
        } else {
          el.value = draft[el.name];
        }
      });
    }

    async handleSubmit(event) {
      // Skip our adapter on the second pass (after Braintree completes,
      // we programmatically call form.submit() — which doesn't fire
      // the submit event again, but guard belt-and-suspenders).
      if (this._submitting) return;
      this._submitting = true;
      event.preventDefault();

      this.dataset.submitting = 'true';

      // Persist the form before we navigate so a Shopify validation
      // error (which reloads the page) doesn't wipe the partner's work.
      this.saveDraft();

      var payload = this.buildPayload();

      try {
        await this.postToBraintree(payload);
      } catch (err) {
        // Log to console but don't block the user. The Shopify contact
        // email is the source-of-truth backup; MDJ admin will still
        // see the application via inbox even if Braintree errored.
        if (window.console && console.warn) {
          console.warn('[heritage-credit-application] Braintree post failed:', err);
        }
      }

      // Submit the form natively. form.submit() doesn't fire the submit
      // event (so our handler doesn't re-enter), which is exactly what
      // we want — Shopify processes the email + redirects.
      this.form.submit();
    }

    /*
     * Walk form.elements, peel `contact[<name>]` to <name>, and build
     * a values map. Skips submit buttons, fieldset shells, and the
     * Shopify form_type hidden inputs.
     */
    readFormValues() {
      var values = {};
      var els = this.form.elements;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (!el.name) continue;
        if (el.type === 'submit' || el.type === 'button' || el.type === 'fieldset') continue;
        var match = el.name.match(CONTACT_NAME);
        var key = match ? match[1] : el.name;
        if (el.type === 'checkbox') {
          values[key] = el.checked ? (el.value || 'yes') : '';
        } else {
          values[key] = (el.value == null ? '' : String(el.value)).trim();
        }
      }
      return values;
    }

    /*
     * Compose the Braintree credit payload.
     *
     *   1. Seed every key in BRAINTREE_FIELDS with "-".
     *   2. Overlay any direct matches from the form (where we set
     *      `name_override` to a Braintree-canonical field name).
     *   3. Derive principle_fname / principle_lname from the single
     *      "Your name" input (split on first space; if no last name
     *      given, lname stays "-").
     *   4. Mirror principle's name onto business_legal_name when the
     *      merchant didn't collect one separately — the legacy form
     *      treated this as a guarantor on the credit line.
     *      Tax ID + JBT map straight through their canonical
     *      BRAINTREE_FIELDS entries (business_federal_id /
     *      business_jbt_number) via the pass-through in step 2.
     *   5. Stash any trade-ref contact name into `trade_ref_N_address_2`
     *      with a "Contact: " prefix AND a custom key
     *      `trade_ref_N_contact_name`. Same belt-and-suspenders
     *      pattern: it shows up in MDJ admin's existing Braintree view
     *      via the address_2 field, and any future consumer reading
     *      the JSON sees the canonical contact_name key.
     *   6. Add the public token + an audit timestamp.
     */
    buildPayload() {
      var values = this.readFormValues();
      var payload = {};

      // 1. Default everything to "-"
      BRAINTREE_FIELDS.forEach(function (key) {
        payload[key] = '-';
      });

      // 2. Pass-through any direct matches
      Object.keys(values).forEach(function (key) {
        if (BRAINTREE_FIELDS.indexOf(key) >= 0) {
          var v = values[key];
          if (v !== '' && v != null) payload[key] = v;
        }
      });

      // 3. Split "name" into principle_fname / principle_lname
      var fullName = (values.name || values.full_name || values.principle_full_name || '').trim();
      if (fullName) {
        var firstSpace = fullName.indexOf(' ');
        if (firstSpace > 0) {
          payload.principle_fname = fullName.slice(0, firstSpace);
          payload.principle_lname = fullName.slice(firstSpace + 1).trim() || '-';
        } else {
          payload.principle_fname = fullName;
        }
      }

      // 4. Mirror principle name onto business_legal_name when blank
      if (payload.business_legal_name === '-' && fullName) {
        payload.business_legal_name = fullName;
      }

      // 4b. Email — the field is contact[email] (Shopify validates that
      // exact name), so it arrives as values.email, not business_email.
      // Map it onto the Braintree business_email key.
      if (values.email && values.email !== '') {
        payload.business_email = values.email;
      }

      // 5. Trade-ref contact names — stash into address_2 + custom key
      [1, 2, 3, 4].forEach(function (n) {
        var contactKey = 'trade_ref_' + n + '_contact_name';
        var contact = values[contactKey] || '';
        if (contact) {
          // Custom JSON key — out-of-spec but carries the data forward
          payload[contactKey] = contact;
          // Mirror into address_2 so MDJ admin's existing Braintree
          // review pane (which shows the spec'd fields) surfaces it.
          var addr2Key = 'trade_ref_' + n + '_address_2';
          if (payload[addr2Key] === '-' || payload[addr2Key] === '') {
            payload[addr2Key] = 'Contact: ' + contact;
          }
        }
      });

      // 6. Public token + audit timestamp
      payload.public_token = this.braintreeToken || '-';
      payload.submitted_at = new Date().toISOString();

      // 7. EasySoft's anti-bot fields (Imran Qureshi, 2026-09-01), from
      // snippets/form-spam-guard.liquid. They arrive in `values` under
      // their own names because they sit outside the contact[...]
      // namespace, but step 1 seeds only BRAINTREE_FIELDS, so they must
      // be copied across explicitly.
      //
      // CAREFUL: an EMPTY hp_client_name is the pass case — a filled one
      // means a bot. It must go over as '' and never inherit the "-"
      // placeholder that step 1 stamps on unknown keys, or every real
      // application reads as a honeypot hit on their side.
      payload.hp_client_name = values.hp_client_name || '';
      payload.hp_time = values.hp_time || '';

      return payload;
    }

    async postToBraintree(payload) {
      if (!this.braintreeUrl || !this.braintreeToken) {
        // Token not configured — skip silently. Shopify contact email
        // still goes through.
        return;
      }
      var controller = new AbortController();
      var timeoutId = window.setTimeout(function () { controller.abort(); }, this.submitTimeout);
      try {
        // Match the legacy form's request shape: `$.post(url, JSON.stringify(data))`
        // sends a form-encoded body containing a JSON string. The
        // Braintree endpoint parses that payload today; preserving the
        // exact wire format avoids regressing the MDJ admin workflow.
        await fetch(this.braintreeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          body: JSON.stringify(payload),
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
          keepalive: true
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
    }
  }

  if (!customElements.get('heritage-credit-application')) {
    customElements.define('heritage-credit-application', HeritageCreditApplication);
  }
})();
